import { Button, DialogActions, DialogContent, DialogTitle, Divider, Modal, ModalDialog, Textarea, Typography } from "@mui/joy";
import React from "react";

interface PackingListItem {
	packing_list_item_id: string;
	line_number: number;
	description: string;
	is_completed: boolean;
}

interface WorkOrderDialogProps {
	open: boolean;
	packingListId: string;
	clientName: string;
	estimateNumber: string;
	typeOfWork: string;
	initialNotes: string;
	onClose: () => void;
	onSaved: () => void;
}

/**
 * Dialog component for viewing work order details
 * Displays client name, estimate number, type of work, and line items fetched from the API
 */
export default function WorkOrderDialog(props: WorkOrderDialogProps) {
	const [items, setItems] = React.useState<PackingListItem[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);
	const [notes, setNotes] = React.useState("");
	const [isSaving, setIsSaving] = React.useState(false);

	// Sync notes state when dialog opens with new data
	React.useEffect(() => {
		if (props.open) setNotes(props.initialNotes ?? "");
	}, [props.open, props.initialNotes]);

	// Fetch line items whenever the dialog opens with a new packing list
	React.useEffect(() => {
		if (!props.open || !props.packingListId) return;

		setIsLoading(true);
		fetch("PackingList/GetPackingListItems", {
			method: "POST",
			cache: "no-cache",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ packingListId: props.packingListId }),
		})
			.then((x) => x.json())
			.then((x: PackingListItem[]) => {
				setItems(x);
				setIsLoading(false);
			});
	}, [props.open, props.packingListId]);

	return (
		<Modal open={props.open}>
			<ModalDialog sx={{ width: 650, maxHeight: "80vh", overflowY: "auto" }}>
				<DialogTitle>Work Order Details</DialogTitle>
				<DialogContent>
					{/* Client and estimate header */}
					<Typography level="title-md" sx={{ mb: 0.5 }}>
						{props.clientName}
					</Typography>
					<Typography level="body-sm" sx={{ mb: 1.5 }}>
						Estimate #{props.estimateNumber}
					</Typography>

					<Divider sx={{ mb: 1.5 }} />

					{/* Type of work */}
					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Type of Work
					</Typography>
					<Typography level="body-sm" sx={{ mb: 1.5 }}>
						{props.typeOfWork || "—"}
					</Typography>

					<Divider sx={{ mb: 1.5 }} />

					{/* Work required line items */}
					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Work Required
					</Typography>
					{isLoading ? (
						<Typography level="body-sm">Loading...</Typography>
					) : items.length === 0 ? (
						<Typography level="body-sm">No items found.</Typography>
					) : (
						items.map((item) => (
							<Typography key={item.packing_list_item_id} level="body-sm" sx={{ mb: 0.25 }}>
								{item.description}
							</Typography>
						))
					)}

					<Divider sx={{ mt: 1.5, mb: 1.5 }} />

					{/* Notes section */}
					<Typography level="title-sm" sx={{ mb: 0.5 }}>
						Notes
					</Typography>
					<Textarea
						minRows={3}
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Enter notes..."
					/>
				</DialogContent>
				<DialogActions>
					<Button
						loading={isSaving}
						onClick={() => {
							setIsSaving(true);
							fetch("PackingList/SavePackingListNotes", {
								method: "POST",
								cache: "no-cache",
								headers: { "Content-Type": "application/json" },
								body: JSON.stringify({ packingListId: props.packingListId, notes }),
							}).then(() => {
								setIsSaving(false);
								props.onSaved();
							});
						}}
						color="success">
						Save Notes
					</Button>
					<Button onClick={props.onClose} color="neutral">
						Close
					</Button>
				</DialogActions>
			</ModalDialog>
		</Modal>
	);
}
